import React from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Collapse,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Alert,
  Stack,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { getRequest, postRequest, deleteRequest } from "Includes/api/apicall";
import axiosAPI from "Includes/api/api";
import { GET_URL, POST_URL } from "Includes/urls";
import CompanyChecklistTab from './CompanyChecklistTab';
import VisitSummaryTab from '../BasicDetails/VisitSummaryTab';
import CompanyReportDetailsTab from './CompanyReportDetailsTab';

function fmtRs(val) {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return val;
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const statusStyle = (val) => {
  const v = String(val || "Active");
  const styles = {
    Active: { bgcolor: "#ECFDF3", color: "#027A48", borderColor: "#A6F4C5" },
    "Low Engagement": { bgcolor: "#EFF8FF", color: "#175CD3", borderColor: "#B2DDFF" },
    "At Risk": { bgcolor: "#FFFAEB", color: "#B54708", borderColor: "#FEDF89" },
    Dead: { bgcolor: "#FEF3F2", color: "#B42318", borderColor: "#FECDCA" },
  };
  return styles[v] || { bgcolor: "#F2F4F7", color: "#344054", borderColor: "#D0D5DD" };
};

const SUMMARY_COLUMN_KEYS = [
  'institute_name',
  'school_address',
  'institute_poc',
  'attendance_used_percentage',
  'diary_used_percentage',
  'last_exam_used_percentage',
  'exam_marks_used_percentage',
];

const formatValue = (value) => {
  if (value === 'Never Used') {
    return <Typography color="error" variant="body2">{value}</Typography>;
  }
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatInvoiceStatus = (code) => {
  const map = {
    DRAFT: "Draft",
    ISSUED: "Issued",
    INVOICED: "Invoiced",
    PARTIAL_PAID: "Partial paid",
    PAID: "Paid",
  };
  return map[String(code || "").toUpperCase()] || code || "—";
};

const invoiceStatusChipSx = (code) => {
  const s = String(code || "").toUpperCase();
  const styles = {
    DRAFT: { bgcolor: "#F2F4F7", color: "#344054" },
    ISSUED: { bgcolor: "#EFF8FF", color: "#175CD3" },
    INVOICED: { bgcolor: "#F4F3FF", color: "#5925DC" },
    PARTIAL_PAID: { bgcolor: "#FFFAEB", color: "#B54708" },
    PAID: { bgcolor: "#ECFDF3", color: "#027A48" },
  };
  return styles[s] || { bgcolor: "#F2F4F7", color: "#344054" };
};

// 🎨 Colored Summary Cards
const SummaryCard = ({ title, value, color }) => (
  <Card
    sx={{
      borderRadius: 3,
      color: "#fff",
      background: color,
      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
    }}
  >
    <CardContent>
      <Typography variant="caption" sx={{ opacity: 0.8 }}>
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        ₹ {fmtRs(value)}
      </Typography>
    </CardContent>
  </Card>
);

const OrderKpiCard = ({ title, value, subtitle, icon, background }) => (
  <Card
    sx={{
      borderRadius: 3,
      color: "#fff",
      background,
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      },
    }}
  >
    <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            opacity: 0.9,
            fontSize: "0.72rem",
          }}
        >
          {title}
        </Typography>
        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.22)",
              borderRadius: "50%",
              p: 0.6,
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ opacity: 0.92, fontWeight: 500, fontSize: "0.75rem" }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default function CompanyBillingDetails(props) {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [payload, setPayload] = React.useState(null);

  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedOrderYear, setSelectedOrderYear] = React.useState("");
  const [selectedInvoiceYear, setSelectedInvoiceYear] = React.useState("");
  const [selectedPaymentYear, setSelectedPaymentYear] = React.useState("");
  const [reports, setReports] = React.useState([]);
  const [companyResources, setCompanyResources] = React.useState([]);
  const [reportColumns, setReportColumns] = React.useState([]);
  const [reportTabs, setReportTabs] = React.useState([]);
  const [expandedRowId, setExpandedRowId] = React.useState(null);
  const [teamReports, setTeamReports] = React.useState([]);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createDialogMode, setCreateDialogMode] = React.useState("order"); // order | invoice | payment
  const [createDialogYear, setCreateDialogYear] = React.useState("");
  const [createDialogLoading, setCreateDialogLoading] = React.useState(false);
  const [setupStudentStrength, setSetupStudentStrength] = React.useState("");
  const [setupCoordinator, setSetupCoordinator] = React.useState("");
  const [setupStudentStrengthError, setSetupStudentStrengthError] = React.useState("");
  const [setupCoordinatorError, setSetupCoordinatorError] = React.useState("");
  const [coordinators, setCoordinators] = React.useState([]);
  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);
  const [downloadRow, setDownloadRow] = React.useState(null);
  const [downloadShowGst, setDownloadShowGst] = React.useState(true);
  const [downloadShowDiscount, setDownloadShowDiscount] = React.useState(true);
  const [downloadShowBillingSummary, setDownloadShowBillingSummary] = React.useState(false);
  const [downloadBillingSummary, setDownloadBillingSummary] = React.useState("");
  const [downloadLoading, setDownloadLoading] = React.useState(false);
  const [selectedInvoices, setSelectedInvoices] = React.useState([]);

  const fetchOverview = React.useCallback(() => {
    if (!companyId) return Promise.resolve();
    const overviewUrl = `${GET_URL.company.api}${companyId}/billing-overview/`;
    return getRequest(overviewUrl, {}, props).then((res) => {
      if (res?.status === 200) {
        setPayload(res?.data?.data || null);
      }
    });
  }, [companyId, props]);

  React.useEffect(() => {
    setSelectedInvoices([]);
  }, [activeTab, selectedInvoiceYear]);

  React.useEffect(() => {
    if (!companyId) {
      setError("Missing company id");
      setLoading(false);
      return;
    }

    setPayload(null);
    setReports([]);
    setLoading(true);
    const overviewUrl = `${GET_URL.company.api}${companyId}/billing-overview/`;
    getRequest(overviewUrl, {}, props)
      .then((res1) => {
        if (res1?.status === 200) {
          setPayload(res1?.data?.data || null);
        } else if (res1) {
          setError(res1?.data?.Reason || "Failed to load overview");
        }
      })
      .catch(() => {
        setError("Failed to load overview");
      })
      .finally(() => {
        setLoading(false);
      });

    // Fetch reports concurrently in background so main billing overview is not delayed
    const reportUrl = GET_URL.client_track_report.api;
    const teamReportUrl = GET_URL.client_track_company_updates.api;

    getRequest(reportUrl, { company: companyId, limit: 1000 }, props)
      .then((res2) => {
        if (res2?.status === 200) {
          setReports(res2?.data?.data || []);
          setReportColumns(res2?.data?.columns || []);
          setReportTabs(res2?.data?.tabs || []);
          setCompanyResources(res2?.data?.company_resource || null);
        }
      })
      .catch(() => null);

    getRequest(teamReportUrl, { company_id: companyId, limit: 100 }, props)
      .then((res3) => {
        if (res3?.status === 200) {
          setTeamReports(Array.isArray(res3.data) ? res3.data : (res3.data?.data || []));
        }
      })
      .catch(() => null);
  }, [companyId, props]);


  const company = payload?.company;
  const years = payload?.years || [];

  React.useEffect(() => {
    if (years.length > 0) {
      if (!selectedOrderYear) setSelectedOrderYear(String(years[0]?.year?.id));
      if (!selectedInvoiceYear) setSelectedInvoiceYear(String(years[0]?.year?.id));
      if (!selectedPaymentYear) setSelectedPaymentYear(String(years[0]?.year?.id));
    }
  }, [years, selectedOrderYear, selectedInvoiceYear, selectedPaymentYear]);

  const activeOrderBlocks = React.useMemo(() => {
    if (!selectedOrderYear || selectedOrderYear === "all") {
      return years;
    }
    return years.filter((b) => String(b.year?.id) === String(selectedOrderYear));
  }, [years, selectedOrderYear]);

  const ordersSummary = React.useMemo(() => {
    let ordersCount = 0;
    let ordersTotal = 0;
    let paymentsCount = 0;
    let paymentsTotal = 0;
    const servicesSet = new Set();

    activeOrderBlocks.forEach((b) => {
      const orders = b.service_orders || [];
      ordersCount += orders.length;
      ordersTotal += parseFloat(b.orders_total || 0) || 0;

      const payments = b.payments || [];
      paymentsCount += payments.length;
      paymentsTotal += parseFloat(b.payments_total || 0) || 0;

      orders.forEach((o) => {
        const name = o.service_name || o.service_plan_name;
        if (name) servicesSet.add(name);
      });
    });

    const balance = ordersTotal - paymentsTotal;

    return {
      ordersCount,
      ordersTotal,
      paymentsCount,
      paymentsTotal,
      balance,
      servicesList: Array.from(servicesSet),
    };
  }, [activeOrderBlocks]);

  const activeInvoiceBlocks = React.useMemo(() => {
    if (!selectedInvoiceYear || selectedInvoiceYear === "all") {
      return years;
    }
    return years.filter((b) => String(b.year?.id) === String(selectedInvoiceYear));
  }, [years, selectedInvoiceYear]);

  const invoicesSummary = React.useMemo(() => {
    let invoicesCount = 0;
    let issuedTotal = 0;
    let paidInvoiceTotal = 0;
    let ordersTotal = 0;
    let paidInvoicesCount = 0;
    let unpaidInvoicesCount = 0;

    activeInvoiceBlocks.forEach((b) => {
      ordersTotal += parseFloat(b.orders_total || 0) || 0;
      paidInvoiceTotal += parseFloat(b.paid_invoice_total || 0) || 0;

      const invoices = (b.issued_invoices || []).filter((inv) => Boolean(inv && inv.id));

      invoicesCount += invoices.length;
      invoices.forEach((inv) => {
        const amt = parseFloat(inv.total_amount || 0) || 0;
        issuedTotal += amt;
        const s = String(inv.status || "").toUpperCase();
        if (s === "PAID") {
          paidInvoicesCount += 1;
        } else {
          unpaidInvoicesCount += 1;
        }
      });
    });

    const pendingTotal = issuedTotal - paidInvoiceTotal;

    return {
      invoicesCount,
      issuedTotal,
      paidInvoiceTotal,
      pendingTotal,
      ordersTotal,
      paidInvoicesCount,
      unpaidInvoicesCount,
    };
  }, [activeInvoiceBlocks]);

  const activePaymentBlocks = React.useMemo(() => {
    if (!selectedPaymentYear || selectedPaymentYear === "all") {
      return years;
    }
    return years.filter((b) => String(b.year?.id) === String(selectedPaymentYear));
  }, [years, selectedPaymentYear]);

  const paymentsSummary = React.useMemo(() => {
    let paymentsCount = 0;
    let paymentsTotal = 0;
    let ordersCount = 0;
    let ordersTotal = 0;
    const methodsSet = new Set();

    activePaymentBlocks.forEach((b) => {
      const payments = b.payments || [];
      paymentsCount += payments.length;
      paymentsTotal += parseFloat(b.payments_total || 0) || 0;

      const orders = b.service_orders || [];
      ordersCount += orders.length;
      ordersTotal += parseFloat(b.orders_total || 0) || 0;

      payments.forEach((p) => {
        if (p.method) methodsSet.add(p.method);
      });
    });

    const balance = ordersTotal - paymentsTotal;

    return {
      paymentsCount,
      paymentsTotal,
      ordersCount,
      ordersTotal,
      balance,
      methodsList: Array.from(methodsSet),
    };
  }, [activePaymentBlocks]);

  const selectedYearBlock = React.useMemo(
    () => years.find((b) => String(b.year?.id) === String(createDialogYear)) || null,
    [years, createDialogYear]
  );

  const needsBillingSetup =
    createDialogMode === "order" && createDialogYear && !selectedYearBlock?.has_setup;

  const fetchCoordinators = React.useCallback(() => {
    getRequest(GET_URL.user.api, { groups: 4 }, props).then((response) => {
      if (response?.status === 200) {
        setCoordinators(response.data?.data || response.data || []);
      }
    });
  }, [props]);

  const resetSetupFields = () => {
    setSetupStudentStrength("");
    setSetupCoordinator("");
    setSetupStudentStrengthError("");
    setSetupCoordinatorError("");
  };

  const validateBillingSetupFields = () => {
    let valid = true;
    setSetupStudentStrengthError("");
    setSetupCoordinatorError("");

    if (!setupStudentStrength || setupStudentStrength === "") {
      setSetupStudentStrengthError("Student strength is required");
      valid = false;
    } else {
      const ss = parseFloat(setupStudentStrength);
      if (
        Number.isNaN(ss) ||
        ss <= 0 ||
        ss > 9999 ||
        !Number.isInteger(ss)
      ) {
        setSetupStudentStrengthError("Enter a whole number between 1 and 9999");
        valid = false;
      }
    }

    if (!setupCoordinator) {
      setSetupCoordinatorError("Coordinator is required");
      valid = false;
    }

    return valid;
  };

  const createBillingSetupAndNavigate = () => {
    if (!validateBillingSetupFields()) return Promise.resolve(null);

    const payload = [
      {
        company: Number(companyId),
        year: createDialogYear,
        student_strength: parseInt(setupStudentStrength, 10),
        coordinator: Number(setupCoordinator),
      },
    ];

    return postRequest(POST_URL.company_billing_config.api, payload, props).then(
      (response) => {
        if (response?.status !== 200) return null;
        const created = response?.data?.data;
        const createdList = Array.isArray(created) ? created : created ? [created] : [];
        const matched = createdList.find(
          (item) => Number(item?.company) === Number(companyId)
        );
        return matched?.id || createdList[0]?.id || null;
      }
    );
  };

  const openCreateDialog = (mode) => {
    const defaultYear =
      years.find((b) => b.has_setup)?.year?.id || years[0]?.year?.id || "";
    setCreateDialogMode(mode);
    setCreateDialogYear(defaultYear);
    resetSetupFields();
    setCreateDialogOpen(true);
    if (mode === "order") {
      fetchCoordinators();
    }
  };

  const handleCreateDialogContinue = () => {
    if (!createDialogYear) {
      Swal.fire("Select year", "Please select an academic year first", "warning");
      return;
    }
    if (!companyId) return;

    if (createDialogMode === "payment") {
      setCreateDialogOpen(false);
      navigate(`/billing/payment?year=${createDialogYear}&company=${companyId}`);
      return;
    }

    if (createDialogMode === "order" && needsBillingSetup) {
      setCreateDialogLoading(true);
      createBillingSetupAndNavigate()
        .then((configId) => {
          if (!configId) {
            Swal.fire(
              "Billing setup failed",
              "Could not create billing setup. Please check the details and try again.",
              "error"
            );
            return;
          }
          setCreateDialogOpen(false);
          resetSetupFields();
          navigate(`/billing/company-setup?config_id=${configId}`);
        })
        .finally(() => setCreateDialogLoading(false));
      return;
    }

    setCreateDialogLoading(true);
    getRequest(
      GET_URL.company_billing_config.api,
      { company: companyId, year: createDialogYear, limit: 1, pageno: 1 },
      props
    )
      .then((res) => {
        const cfg = res?.data?.data?.[0];
        setCreateDialogOpen(false);
        if (createDialogMode === "invoice") {
          if (cfg?.id) {
            navigate(`/billing/issue-invoice/?config_id=${cfg.id}`);
          } else {
            Swal.fire(
              "Billing setup required",
              "Create a billing setup / order for this company and year before issuing an invoice.",
              "warning"
            ).then(() => {
              navigate("/billing/company-billing-config-add", {
                state: { agreementOrderFlow: { companyId: Number(companyId) } },
              });
            });
          }
          return;
        }
        // create order
        if (cfg?.id) {
          navigate(`/billing/company-setup?config_id=${cfg.id}`);
        } else {
          navigate("/billing/company-billing-config-add", {
            state: { agreementOrderFlow: { companyId: Number(companyId) } },
          });
        }
      })
      .finally(() => setCreateDialogLoading(false));
  };

  const dialogTitle =
    createDialogMode === "invoice"
      ? "Create Invoice"
      : createDialogMode === "payment"
        ? "Add Payment"
        : "Create Order";

  const dialogContinueLabel =
    createDialogMode === "payment"
      ? "Continue to Payment"
      : needsBillingSetup
        ? "Create Setup & Continue"
        : "Continue";

  const handleEditOrViewInvoice = (inv, blockYear) => {
    if (inv?.order_id || inv?.order_v2_id) {
      navigate(`/billing/orders-v2/form?id=${inv.order_id || inv.order_v2_id}`);
      return;
    }
    if (inv?.invoice_number && inv.invoice_number.startsWith("INV-V2-")) {
      const parts = inv.invoice_number.split("-");
      if (parts[2]) {
        const idVal = parseInt(parts[2], 10);
        if (!isNaN(idVal)) {
          navigate(`/billing/orders-v2/form?id=${idVal}`);
          return;
        }
      }
      navigate(`/billing/orders-v2/form`);
      return;
    }

    // Look for an Order V2 in the year block
    const targetYearId = blockYear?.id || selectedInvoiceYear;
    const yearBlock = years.find((b) => String(b.year?.id) === String(targetYearId));
    const v2Order = (yearBlock?.service_orders || []).find((o) => Boolean(o.order_v2_id));
    if (v2Order?.order_v2_id) {
      navigate(`/billing/orders-v2/form?id=${v2Order.order_v2_id}`);
      return;
    }

    // Check across all years if this company has an Order V2
    for (const yb of years) {
      const foundOrder = (yb.service_orders || []).find((o) => Boolean(o.order_v2_id));
      if (foundOrder?.order_v2_id) {
        navigate(`/billing/orders-v2/form?id=${foundOrder.order_v2_id}`);
        return;
      }
    }

    const yrParam = targetYearId && targetYearId !== "all" ? `&year=${targetYearId}` : "";
    navigate(`/billing/orders-v2/form?company=${companyId}${yrParam}`);
  };

  const handlePrintInvoice = (inv, e) => {
    if (e) e.stopPropagation();
    if (!inv?.id) return;
    setDownloadRow(inv);
    setDownloadShowGst(true);
    setDownloadShowDiscount(true);
    setDownloadShowBillingSummary(false);
    setDownloadBillingSummary("");
    setDownloadDialogOpen(true);
  };

  const handleDownloadDialogClose = () => {
    if (downloadLoading) return;
    setDownloadDialogOpen(false);
    setDownloadRow(null);
  };

  const handleDownloadInvoiceSubmit = () => {
    if (!downloadRow?.id) return;
    setDownloadLoading(true);
    const url = `${GET_URL.issued_invoices.api}${downloadRow.id}/`;
    const reqProps = { ...props, responseType: "blob" };
    const requestParams = {
      generate_pdf: 1,
      show_gst: downloadShowGst ? 1 : 0,
      show_discount: downloadShowDiscount ? 1 : 0,
      show_billing_summary: downloadShowBillingSummary ? 1 : 0,
      ...(downloadShowBillingSummary && downloadBillingSummary.trim()
        ? { billing_summary: downloadBillingSummary.trim() }
        : {}),
    };

    getRequest(url, requestParams, reqProps)
      .then((response) => {
        if (response && response.status === 200) {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const fileURL = window.URL.createObjectURL(blob);
          const contentDisposition =
            response?.headers?.["content-disposition"] ||
            response?.headers?.["Content-Disposition"];
          const match =
            typeof contentDisposition === "string"
              ? contentDisposition.match(/filename="?([^"]+)"?/i)
              : null;
          const filename =
            match?.[1] ||
            `invoice_${downloadRow.invoice_number || downloadRow.id}.pdf`;
          const link = document.createElement("a");
          link.href = fileURL;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(link);
          setDownloadDialogOpen(false);
          setDownloadRow(null);
        }
      })
      .catch((error) => {
        console.error("Error downloading invoice PDF:", error);
        Swal.fire("Error", "Failed to download invoice PDF", "error");
      })
      .finally(() => setDownloadLoading(false));
  };

  const handleSelectAllInvoices = (invoiceList) => {
    const allIds = (invoiceList || []).map((inv) => inv.id);
    const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedInvoices.includes(id));
    if (isAllSelected) {
      setSelectedInvoices((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedInvoices((prev) => Array.from(new Set([...prev, ...allIds])));
    }
  };

  const handleToggleSelectInvoice = (id, e) => {
    if (e) e.stopPropagation();
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteSingleInvoice = (inv) => {
    if (!inv?.id) return;
    deleteRequest(`${GET_URL.issued_invoices.api}${inv.id}/`, {}, props)
      .then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
          Swal.fire({
            icon: "success",
            title: "Invoice Deleted",
            text: `Invoice ${inv.invoice_number || inv.id} deleted successfully.`,
            timer: 1500,
            showConfirmButton: false,
          });
          setSelectedInvoices((prev) => prev.filter((id) => id !== inv.id));
          fetchOverview();
        }
      })
      .catch((err) => {
        console.error("Delete invoice error:", err);
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: err?.response?.data?.Reason || err?.response?.data?.detail || "Could not delete invoice.",
        });
      });
  };

  const handleBulkDeleteInvoices = () => {
    if (!selectedInvoices.length) return;
    Swal.fire({
      title: "Delete Selected Invoices?",
      text: `Are you sure you want to delete ${selectedInvoices.length} selected invoice(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748b",
      confirmButtonText: `Yes, delete ${selectedInvoices.length} invoice(s)`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting invoices...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        let failedCount = 0;
        for (const invId of selectedInvoices) {
          try {
            await axiosAPI.delete(`${GET_URL.issued_invoices.api}${invId}/`);
          } catch (err) {
            console.error(`Failed to delete invoice ${invId}:`, err);
            failedCount += 1;
          }
        }

        Swal.close();
        if (failedCount === 0) {
          Swal.fire({
            icon: "success",
            title: "Invoices Deleted",
            text: `${selectedInvoices.length} invoice(s) deleted successfully.`,
            timer: 1800,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "warning",
            title: "Partially Completed",
            text: `${selectedInvoices.length - failedCount} deleted successfully. ${failedCount} could not be deleted.`,
          });
        }
        setSelectedInvoices([]);
        fetchOverview();
      }
    });
  };

  const summaryColumns = React.useMemo(() => {
    const selectedColumns = (reportColumns || []).filter((item) =>
      SUMMARY_COLUMN_KEYS.includes(item.schemacolumn)
    );
    // Sort columns to match SUMMARY_COLUMN_KEYS order if possible
    selectedColumns.sort((a, b) => {
      return SUMMARY_COLUMN_KEYS.indexOf(a.schemacolumn) - SUMMARY_COLUMN_KEYS.indexOf(b.schemacolumn);
    });
    return selectedColumns.length
      ? selectedColumns
      : (reportColumns || []).slice(0, 6);
  }, [reportColumns]);

  const detailColumns = React.useMemo(() => {
    const summaryKeys = new Set(summaryColumns.map((item) => item.schemacolumn));
    return (reportColumns || []).filter((item) => !summaryKeys.has(item.schemacolumn));
  }, [reportColumns, summaryColumns]);
  console.log("companyResources:", companyResources);
  console.log("reports:", reports);
  console.log("Report field names:", reports.length > 0 ? Object.keys(reports[0]) : "No reports");

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* BACK BUTTON */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/company/list")}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && company && (
        <>
          {/* 🌈 HEADER */}
          <Card
            sx={{
              borderRadius: 4,
              mb: 3,
              color: "#fff",
              background:
                "blue",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {company.name}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Code: {company.code} · Domain: {company.domain || "—"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => openCreateDialog("order")}
                    sx={{ color: "#1d4ed8", fontWeight: 600 }}
                  >
                    Create Order
                  </Button>
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => openCreateDialog("invoice")}
                    sx={{ color: "#1d4ed8", fontWeight: 600 }}
                  >
                    Create Invoice
                  </Button>
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => openCreateDialog("payment")}
                    sx={{ color: "#1d4ed8", fontWeight: 600 }}
                  >
                    Add Payment
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={
                    company.payment_gateway_enabled
                      ? "Gateway Enabled"
                      : "Gateway Disabled"
                  }
                  sx={{
                    background: "#fff",
                    color: "#333",
                  }}
                />
                <Chip
                  label={company.is_active ? "Active" : "Inactive"}
                  sx={{
                    background: company.is_active ? "#22c55e" : "#ef4444",
                    color: "#fff",
                  }}
                />
                <Chip
                  label={`Team: ${company.status_by_team || "Active"}`}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    borderWidth: 1,
                    ...statusStyle(company.status_by_team),
                  }}
                />
                <Chip
                  label={`System: ${company.status_by_system || "Active"}`}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    borderWidth: 1,
                    ...statusStyle(company.status_by_system),
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => {
                setActiveTab(val);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTab-root": {
                  width: 120,
                  minWidth: 120,
                },
              }}
            >
              <Tab label="Orders" />
              <Tab label="Invoice" />
              <Tab label="Payment" />
              <Tab label="Report" />
              <Tab label="Team Report" />
              <Tab label="Checklist" />
              <Tab label="Visits" />
              <Tab label="Messages"/>
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Box>
              {/* 🎯 ORDERS TOP BAR WITH YEAR DROPDOWN */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  background: "linear-gradient(to right, #ffffff, #f8fafc)",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarMonthOutlinedIcon sx={{ color: "#4f46e5", fontSize: 24 }} />
                    <Typography variant="h6" fontWeight={700} color="#0f172a">
                      Orders Summary & Details
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedOrderYear === "all"
                      ? "Aggregated summary for all academic years"
                      : `Selected Year: ${
                          years.find((b) => String(b.year?.id) === String(selectedOrderYear))?.year?.name ||
                          selectedOrderYear
                        }`}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 220, bgcolor: "#fff", borderRadius: 2 }}>
                    <InputLabel id="order-year-select-label">Academic Year</InputLabel>
                    <Select
                      labelId="order-year-select-label"
                      value={selectedOrderYear || "all"}
                      label="Academic Year"
                      onChange={(e) => setSelectedOrderYear(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">
                        <em>All Academic Years</em>
                      </MenuItem>
                      {years.map((b) => (
                        <MenuItem key={b.year?.id} value={String(b.year?.id)}>
                          {b.year?.name} {b.year?.code ? `(${b.year?.code})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => {
                      const targetYear =
                        selectedOrderYear && selectedOrderYear !== "all"
                          ? selectedOrderYear
                          : years[0]?.year?.id || "";
                      setCreateDialogYear(targetYear);
                      openCreateDialog("order");
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                    }}
                  >
                    Create Order
                  </Button>
                </Box>
              </Paper>

              {/* 📊 YEAR-BASED SUMMARY METRICS CARDS */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Total Orders"
                    value={`${ordersSummary.ordersCount} ${
                      ordersSummary.ordersCount === 1 ? "Order" : "Orders"
                    }`}
                    subtitle={`Total Billed: ₹ ${fmtRs(ordersSummary.ordersTotal)}`}
                    icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Total Payments"
                    value={`₹ ${fmtRs(ordersSummary.paymentsTotal)}`}
                    subtitle={`${ordersSummary.paymentsCount} recorded transaction${
                      ordersSummary.paymentsCount === 1 ? "" : "s"
                    }`}
                    icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Outstanding Balance"
                    value={`₹ ${fmtRs(Math.abs(ordersSummary.balance))}`}
                    subtitle={
                      ordersSummary.balance > 0
                        ? "Pending collection"
                        : ordersSummary.balance < 0
                        ? "Advance received"
                        : "Fully settled"
                    }
                    icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                    background={
                      ordersSummary.balance > 0
                        ? "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)"
                        : "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Active Services"
                    value={`${ordersSummary.servicesList.length} ${
                      ordersSummary.servicesList.length === 1 ? "Service" : "Services"
                    }`}
                    subtitle={
                      ordersSummary.servicesList.length > 0
                        ? ordersSummary.servicesList.slice(0, 2).join(", ") +
                          (ordersSummary.servicesList.length > 2
                            ? ` +${ordersSummary.servicesList.length - 2} more`
                            : "")
                        : "No services ordered"
                    }
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)"
                  />
                </Grid>
              </Grid>

              {/* 📋 ORDERS DETAILS ACCORDING TO SELECTED YEAR */}
              {selectedOrderYear !== "all" ? (
                // Single Selected Year View
                (() => {
                  const block = activeOrderBlocks[0];
                  if (!block) {
                    return (
                      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px dashed #cbd5e1" }}>
                        <Typography color="text.secondary">
                          No order data available for the selected academic year.
                        </Typography>
                      </Paper>
                    );
                  }

                  const balance = (block.orders_total || 0) - (block.payments_total || 0);

                  return (
                    <Paper
                      elevation={1}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                          borderBottom: "1px solid #e2e8f0",
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Typography variant="h6" fontWeight={800} color="#0f172a">
                            {block.year?.name}
                          </Typography>
                          <Chip
                            label={block.has_setup ? "Setup Active" : "No Billing Setup"}
                            size="small"
                            color={block.has_setup ? "success" : "default"}
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            label={`Balance: ₹ ${fmtRs(balance)}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: balance >= 0 ? "#eff6ff" : "#fef2f2",
                              color: balance >= 0 ? "#1d4ed8" : "#b91c1c",
                              border: "1px solid",
                              borderColor: balance >= 0 ? "#bfdbfe" : "#fecaca",
                            }}
                          />
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={() => {
                              setCreateDialogYear(String(block.year?.id));
                              openCreateDialog("order");
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                          >
                            Add Order
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<PaymentOutlinedIcon />}
                            onClick={() => {
                              setCreateDialogYear(String(block.year?.id));
                              openCreateDialog("payment");
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                          >
                            Add Payment
                          </Button>
                        </Box>
                      </Box>

                      <Box sx={{ p: 3 }}>
                        {!block.has_setup ? (
                          <Alert
                            severity="info"
                            sx={{ borderRadius: 2 }}
                            action={
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                  setCreateDialogYear(String(block.year?.id));
                                  openCreateDialog("order");
                                }}
                              >
                                Configure Setup
                              </Button>
                            }
                          >
                            This academic year does not have billing setup configured yet. Click "Configure Setup" or "Add Order" to begin.
                          </Alert>
                        ) : (
                          <>
                            {/* 🧾 SERVICES & ORDERS TABLE */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                              <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                                Services & Orders
                              </Typography>
                              <Chip
                                label={`${(block.service_orders || []).length} ordered`}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>

                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2, mb: 3 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Order Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(block.service_orders || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                        No service orders found for {block.year?.name}
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    <>
                                      {block.service_orders.map((o, i) => (
                                        <TableRow
                                          key={o.id}
                                          hover
                                          sx={{
                                            background: i % 2 === 0 ? "#ffffff" : "#fbfcfd",
                                          }}
                                        >
                                          <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                                            {o.service_name}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={o.service_plan_name || "Standard"}
                                              size="small"
                                              variant="outlined"
                                              sx={{ fontSize: "0.75rem", height: 22 }}
                                            />
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700, color: "#4f46e5" }}>
                                            ₹ {fmtRs(o.total_amount)}
                                          </TableCell>
                                          <TableCell color="text.secondary">{o.order_date || "—"}</TableCell>
                                          <TableCell color="text.secondary">
                                            {o.notes && o.notes.startsWith("__ID_CARD_CONFIG__") ? (
                                              <Chip
                                                label="ID Card Configured"
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: "0.7rem", height: 20, bgcolor: "#f1f5f9", color: "#475569" }}
                                              />
                                            ) : (
                                              o.notes || "—"
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ background: "#f8fafc" }}>
                                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                                          Total Orders Amount:
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: "#4338ca", fontSize: "0.95rem" }}>
                                          ₹ {fmtRs(block.orders_total)}
                                        </TableCell>
                                        <TableCell colSpan={2} />
                                      </TableRow>
                                    </>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Divider sx={{ my: 3 }} />

                            {/* 💳 PAYMENTS TABLE */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                              <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                                Payments Received
                              </Typography>
                              <Chip
                                label={`${(block.payments || []).length} record${
                                  (block.payments || []).length === 1 ? "" : "s"
                                }`}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>

                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(block.payments || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                        No payments recorded for {block.year?.name}
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    <>
                                      {block.payments.map((p, i) => (
                                        <TableRow
                                          key={p.id}
                                          hover
                                          sx={{
                                            background: i % 2 === 0 ? "#ffffff" : "#fbfcfd",
                                          }}
                                        >
                                          <TableCell>{p.payment_date || "—"}</TableCell>
                                          <TableCell>
                                            <Chip
                                              label={p.method || "Payment"}
                                              size="small"
                                              variant="outlined"
                                              color="success"
                                              sx={{ fontSize: "0.75rem", height: 22 }}
                                            />
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700, color: "#16a34a" }}>
                                            ₹ {fmtRs(p.amount)}
                                          </TableCell>
                                          <TableCell>{p.reference || "—"}</TableCell>
                                          <TableCell>{p.status || "Completed"}</TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ background: "#f8fafc" }}>
                                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                                          Total Payments Received:
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: "#15803d", fontSize: "0.95rem" }}>
                                          ₹ {fmtRs(block.payments_total)}
                                        </TableCell>
                                        <TableCell colSpan={2} />
                                      </TableRow>
                                    </>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </Box>
                    </Paper>
                  );
                })()
              ) : (
                // All Academic Years Accordion View
                years.map((block) => {
                  const balance = (block.orders_total || 0) - (block.payments_total || 0);

                  return (
                    <Accordion
                      key={block.year?.id}
                      defaultExpanded={block === years[0]}
                      sx={{
                        mb: 2.5,
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography fontWeight={800} color="#0f172a">
                              {block.year?.name}
                            </Typography>
                            <Chip
                              label={block.has_setup ? "Setup Active" : "No Setup"}
                              size="small"
                              color={block.has_setup ? "success" : "default"}
                              sx={{ fontWeight: 600, fontSize: "0.7rem", height: 20 }}
                            />
                          </Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: balance >= 0 ? "#16a34a" : "#dc2626",
                              fontWeight: 700,
                            }}
                          >
                            Balance: ₹ {fmtRs(balance)}
                          </Typography>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails sx={{ background: "#fff", p: 3 }}>
                        {!block.has_setup ? (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No billing setup available for {block.year?.name}
                          </Alert>
                        ) : (
                          <>
                            {/* MINI SUMMARY */}
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                              <Grid item xs={12} md={4}>
                                <SummaryCard
                                  title="Orders Total"
                                  value={block.orders_total}
                                  color="linear-gradient(135deg,#6366f1,#4f46e5)"
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <SummaryCard
                                  title="Payments Total"
                                  value={block.payments_total}
                                  color="linear-gradient(135deg,#22c55e,#16a34a)"
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <SummaryCard
                                  title="Balance"
                                  value={balance}
                                  color={
                                    balance >= 0
                                      ? "linear-gradient(135deg,#0ea5e9,#0284c7)"
                                      : "linear-gradient(135deg,#ef4444,#dc2626)"
                                  }
                                />
                              </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* SERVICES */}
                            <Typography fontWeight={700} sx={{ mb: 1, color: "#1e293b" }}>
                              Services & Orders
                            </Typography>
                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2, mb: 3 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(block.service_orders || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={4} align="center" sx={{ py: 2, color: "text.secondary" }}>
                                        No orders found
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    block.service_orders.map((o, i) => (
                                      <TableRow
                                        key={o.id}
                                        hover
                                        sx={{
                                          background: i % 2 === 0 ? "#ffffff" : "#f9fafb",
                                        }}
                                      >
                                        <TableCell sx={{ fontWeight: 600 }}>{o.service_name}</TableCell>
                                        <TableCell>{o.service_plan_name}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: "#4f46e5" }}>
                                          ₹ {fmtRs(o.total_amount)}
                                        </TableCell>
                                        <TableCell>{o.order_date || "—"}</TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Divider sx={{ my: 2 }} />

                            {/* PAYMENTS */}
                            <Typography fontWeight={700} sx={{ mb: 1, color: "#1e293b" }}>
                              Payments
                            </Typography>
                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(block.payments || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={4} align="center" sx={{ py: 2, color: "text.secondary" }}>
                                        No payments found
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    block.payments.map((p, i) => (
                                      <TableRow
                                        key={p.id}
                                        hover
                                        sx={{
                                          background: i % 2 === 0 ? "#ffffff" : "#f9fafb",
                                        }}
                                      >
                                        <TableCell>{p.payment_date || "—"}</TableCell>
                                        <TableCell>{p.method}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: "#16a34a" }}>
                                          ₹ {fmtRs(p.amount)}
                                        </TableCell>
                                        <TableCell>{p.reference || "—"}</TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {/* 🎯 INVOICES TOP BAR WITH YEAR DROPDOWN */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  background: "linear-gradient(to right, #ffffff, #f8fafc)",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReceiptLongOutlinedIcon sx={{ color: "#2563eb", fontSize: 24 }} />
                    <Typography variant="h6" fontWeight={700} color="#0f172a">
                      Invoices Summary & Details
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedInvoiceYear === "all"
                      ? "Aggregated summary for all academic years"
                      : `Selected Year: ${
                          years.find((b) => String(b.year?.id) === String(selectedInvoiceYear))?.year?.name ||
                          selectedInvoiceYear
                        }`}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 220, bgcolor: "#fff", borderRadius: 2 }}>
                    <InputLabel id="invoice-year-select-label">Academic Year</InputLabel>
                    <Select
                      labelId="invoice-year-select-label"
                      value={selectedInvoiceYear || "all"}
                      label="Academic Year"
                      onChange={(e) => setSelectedInvoiceYear(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">
                        <em>All Academic Years</em>
                      </MenuItem>
                      {years.map((b) => (
                        <MenuItem key={b.year?.id} value={String(b.year?.id)}>
                          {b.year?.name} {b.year?.code ? `(${b.year?.code})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => {
                      const targetYear =
                        selectedInvoiceYear && selectedInvoiceYear !== "all"
                          ? selectedInvoiceYear
                          : years[0]?.year?.id || "";
                      setCreateDialogYear(targetYear);
                      openCreateDialog("invoice");
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    }}
                  >
                    Create Invoice
                  </Button>
                </Box>
              </Paper>

              {/* 📊 INVOICES SUMMARY METRICS CARDS */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Total Invoiced"
                    value={`₹ ${fmtRs(invoicesSummary.issuedTotal)}`}
                    subtitle={`${invoicesSummary.invoicesCount} ${
                      invoicesSummary.invoicesCount === 1 ? "invoice" : "invoices"
                    } issued`}
                    icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Paid Amount"
                    value={`₹ ${fmtRs(invoicesSummary.paidInvoiceTotal)}`}
                    subtitle={`${invoicesSummary.paidInvoicesCount} paid invoice${
                      invoicesSummary.paidInvoicesCount === 1 ? "" : "s"
                    }`}
                    icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Pending Due"
                    value={`₹ ${fmtRs(Math.max(0, invoicesSummary.pendingTotal))}`}
                    subtitle={`${invoicesSummary.unpaidInvoicesCount} invoice${
                      invoicesSummary.unpaidInvoicesCount === 1 ? "" : "s"
                    } pending`}
                    icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                    background={
                      invoicesSummary.pendingTotal > 0
                        ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        : "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Orders Billed"
                    value={`₹ ${fmtRs(invoicesSummary.ordersTotal)}`}
                    subtitle="Total orders value for year"
                    icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
                  />
                </Grid>
              </Grid>

              {/* 📋 INVOICE DETAILS ACCORDING TO SELECTED YEAR */}
              {selectedInvoiceYear !== "all" ? (
                // Single Selected Year View
                (() => {
                  const block = activeInvoiceBlocks[0];
                  if (!block) {
                    return (
                      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px dashed #cbd5e1" }}>
                        <Typography color="text.secondary">
                          No invoice data available for the selected academic year.
                        </Typography>
                      </Paper>
                    );
                  }

                  const invoices = (block.issued_invoices || []).filter((inv) => Boolean(inv && inv.id));

                  return (
                    <Paper
                      elevation={1}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                          borderBottom: "1px solid #e2e8f0",
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Typography variant="h6" fontWeight={800} color="#0f172a">
                            {block.year?.name}
                          </Typography>
                          <Chip
                            label={block.has_setup ? "Setup Active" : "No Billing Setup"}
                            size="small"
                            color={block.has_setup ? "success" : "default"}
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            label={`Issued: ₹ ${fmtRs(block.issued_total)} · Paid: ₹ ${fmtRs(block.paid_invoice_total)}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: "#eff6ff",
                              color: "#1d4ed8",
                              border: "1px solid #bfdbfe",
                            }}
                          />
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddCircleOutlineOutlinedIcon />}
                          onClick={() => {
                            setCreateDialogYear(String(block.year?.id));
                            openCreateDialog("invoice");
                          }}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                        >
                          Create Invoice
                        </Button>
                      </Box>

                      <Box sx={{ p: 3 }}>
                        {!block.has_setup ? (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No billing setup available for {block.year?.name}.
                          </Alert>
                        ) : (
                          <>
                            {selectedInvoices.length > 0 && (
                              <Paper
                                elevation={0}
                                sx={{
                                  mb: 2,
                                  p: 1.5,
                                  px: 2.5,
                                  bgcolor: "#fef2f2",
                                  border: "1px solid #fecaca",
                                  borderRadius: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  flexWrap: "wrap",
                                  gap: 1.5,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#991b1b" }}>
                                  {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? "s" : ""} selected
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => setSelectedInvoices([])}
                                    sx={{ textTransform: "none", fontWeight: 600, color: "#475569" }}
                                  >
                                    Deselect All
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteOutlineOutlinedIcon />}
                                    onClick={handleBulkDeleteInvoices}
                                    sx={{ textTransform: "none", fontWeight: 700, boxShadow: "none" }}
                                  >
                                    Delete Selected ({selectedInvoices.length})
                                  </Button>
                                </Box>
                              </Paper>
                            )}

                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                              <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                                Invoices List
                              </Typography>
                              <Chip
                                label={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>

                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell padding="checkbox" sx={{ width: 48 }}>
                                      <Checkbox
                                        size="small"
                                        indeterminate={
                                          selectedInvoices.length > 0 &&
                                          invoices.some((inv) => selectedInvoices.includes(inv.id)) &&
                                          !invoices.every((inv) => selectedInvoices.includes(inv.id))
                                        }
                                        checked={
                                          invoices.length > 0 &&
                                          invoices.every((inv) => selectedInvoices.includes(inv.id))
                                        }
                                        onChange={() => handleSelectAllInvoices(invoices)}
                                        sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#2563eb" } }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Issue Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell align="center" width={140} sx={{ fontWeight: 700 }}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {invoices.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                        No invoices found for {block.year?.name}
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    <>
                                      {invoices.map((inv, i) => {
                                        const isSelected = selectedInvoices.includes(inv.id);
                                        return (
                                          <TableRow
                                            key={inv.id}
                                            hover
                                            selected={isSelected}
                                            sx={{
                                              cursor: "pointer",
                                              background: i % 2 === 0 ? "#ffffff" : "#fbfcfd",
                                              "&.Mui-selected": { bgcolor: "#eff6ff !important" },
                                            }}
                                            onClick={() => {
                                              handleEditOrViewInvoice(inv, block.year);
                                            }}
                                          >
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                              <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onChange={(e) => handleToggleSelectInvoice(inv.id, e)}
                                                sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#2563eb" } }}
                                              />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                                              {inv.invoice_number || "—"}
                                            </TableCell>
                                            <TableCell color="text.secondary">{inv.issue_date || "—"}</TableCell>
                                            <TableCell color="text.secondary">{inv.due_date || "—"}</TableCell>
                                            <TableCell>
                                              <Chip
                                                size="small"
                                                label={formatInvoiceStatus(inv.status)}
                                                sx={{
                                                  fontWeight: 600,
                                                  ...invoiceStatusChipSx(inv.status),
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                              ₹ {fmtRs(inv.total_amount)}
                                            </TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                              <Box sx={{ display: "inline-flex", gap: 0.5, alignItems: "center" }}>
                                                <Tooltip title="Edit invoice">
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleEditOrViewInvoice(inv, block.year);
                                                    }}
                                                    sx={{ "&:hover": { bgcolor: "#eff6ff" } }}
                                                  >
                                                    <EditOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Print / Download invoice PDF">
                                                  <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    onClick={(e) => handlePrintInvoice(inv, e)}
                                                    sx={{ "&:hover": { bgcolor: "#f3e8ff" } }}
                                                  >
                                                    <PrintOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete invoice">
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteSingleInvoice(inv);
                                                    }}
                                                    sx={{ "&:hover": { bgcolor: "#fef2f2" } }}
                                                  >
                                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                      <TableRow sx={{ background: "#f8fafc" }}>
                                        <TableCell padding="checkbox" />
                                        <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                                          Total Invoices Amount:
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: "#1d4ed8", fontSize: "0.95rem" }}>
                                          ₹ {fmtRs(invoices.reduce((acc, inv) => acc + (parseFloat(inv.total_amount) || 0), 0))}
                                        </TableCell>
                                        <TableCell />
                                      </TableRow>
                                    </>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </Box>
                    </Paper>
                  );
                })()
              ) : (
                // All Academic Years Accordion View
                <>
                  {selectedInvoices.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        mb: 2.5,
                        p: 1.5,
                        px: 2.5,
                        bgcolor: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#991b1b" }}>
                        {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? "s" : ""} selected
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => setSelectedInvoices([])}
                          sx={{ textTransform: "none", fontWeight: 600, color: "#475569" }}
                        >
                          Deselect All
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<DeleteOutlineOutlinedIcon />}
                          onClick={handleBulkDeleteInvoices}
                          sx={{ textTransform: "none", fontWeight: 700, boxShadow: "none" }}
                        >
                          Delete Selected ({selectedInvoices.length})
                        </Button>
                      </Box>
                    </Paper>
                  )}

                  {years.map((block) => {
                    const invoices = (block.issued_invoices || []).filter((inv) => Boolean(inv && inv.id));

                    return (
                      <Accordion
                        key={`inv-${block.year?.id}`}
                        defaultExpanded={block === years[0]}
                        sx={{
                          mb: 2.5,
                          borderRadius: 3,
                          overflow: "hidden",
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Typography fontWeight={800} color="#0f172a">
                                {block.year?.name}
                              </Typography>
                              <Chip
                                label={block.has_setup ? "Setup Active" : "No Setup"}
                                size="small"
                                color={block.has_setup ? "success" : "default"}
                                sx={{ fontWeight: 600, fontSize: "0.7rem", height: 20 }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Issued: ₹ {fmtRs(block.issued_total)} · Paid: ₹ {fmtRs(block.paid_invoice_total)}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ background: "#fff", p: 3 }}>
                          {!block.has_setup ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              No billing setup available for {block.year?.name}
                            </Alert>
                          ) : (
                            <>
                              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                <Grid item xs={12} md={6}>
                                  <SummaryCard
                                    title="Issued"
                                    value={block.issued_total}
                                    color="linear-gradient(135deg,#3b82f6,#2563eb)"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <SummaryCard
                                    title="Paid"
                                    value={block.paid_invoice_total}
                                    color="linear-gradient(135deg,#22c55e,#16a34a)"
                                  />
                                </Grid>
                              </Grid>

                              <Divider sx={{ my: 2 }} />

                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                <Typography fontWeight={700} sx={{ color: "#1e293b" }}>
                                  Invoices
                                </Typography>
                                <Chip
                                  label={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Box>

                              <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ background: "#f8fafc" }}>
                                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                                        <Checkbox
                                          size="small"
                                          indeterminate={
                                            selectedInvoices.length > 0 &&
                                            invoices.some((inv) => selectedInvoices.includes(inv.id)) &&
                                            !invoices.every((inv) => selectedInvoices.includes(inv.id))
                                          }
                                          checked={
                                            invoices.length > 0 &&
                                            invoices.every((inv) => selectedInvoices.includes(inv.id))
                                          }
                                          onChange={() => handleSelectAllInvoices(invoices)}
                                          sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#2563eb" } }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Issue Date</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                      <TableCell align="center" width={140} sx={{ fontWeight: 700 }}>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {invoices.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 2, color: "text.secondary" }}>
                                          No invoices found
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      <>
                                        {invoices.map((inv, i) => {
                                          const isSelected = selectedInvoices.includes(inv.id);
                                          return (
                                            <TableRow
                                              key={inv.id}
                                              hover
                                              selected={isSelected}
                                              sx={{
                                                cursor: "pointer",
                                                background: i % 2 === 0 ? "#ffffff" : "#f9fafb",
                                                "&.Mui-selected": { bgcolor: "#eff6ff !important" },
                                              }}
                                              onClick={() => {
                                                handleEditOrViewInvoice(inv, block.year);
                                              }}
                                            >
                                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                  size="small"
                                                  checked={isSelected}
                                                  onChange={(e) => handleToggleSelectInvoice(inv.id, e)}
                                                  sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#2563eb" } }}
                                                />
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600 }}>{inv.invoice_number || "—"}</TableCell>
                                              <TableCell>{inv.issue_date || "—"}</TableCell>
                                              <TableCell>{inv.due_date || "—"}</TableCell>
                                              <TableCell>
                                                <Chip
                                                  size="small"
                                                  label={formatInvoiceStatus(inv.status)}
                                                  sx={{
                                                    fontWeight: 600,
                                                    ...invoiceStatusChipSx(inv.status),
                                                  }}
                                                />
                                              </TableCell>
                                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                ₹ {fmtRs(inv.total_amount)}
                                              </TableCell>
                                              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: "inline-flex", gap: 0.5, alignItems: "center" }}>
                                                  <Tooltip title="Edit invoice">
                                                    <IconButton
                                                      size="small"
                                                      color="primary"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditOrViewInvoice(inv, block.year);
                                                      }}
                                                      sx={{ "&:hover": { bgcolor: "#eff6ff" } }}
                                                    >
                                                      <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Print / Download invoice PDF">
                                                    <IconButton
                                                      size="small"
                                                      color="secondary"
                                                      onClick={(e) => handlePrintInvoice(inv, e)}
                                                      sx={{ "&:hover": { bgcolor: "#f3e8ff" } }}
                                                    >
                                                      <PrintOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Delete invoice">
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSingleInvoice(inv);
                                                      }}
                                                      sx={{ "&:hover": { bgcolor: "#fef2f2" } }}
                                                    >
                                                      <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                        <TableRow sx={{ background: "#f8fafc" }}>
                                          <TableCell padding="checkbox" />
                                          <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                                            Total Invoices Amount:
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 800, color: "#1d4ed8", fontSize: "0.95rem" }}>
                                            ₹ {fmtRs(invoices.reduce((acc, inv) => acc + (parseFloat(inv.total_amount) || 0), 0))}
                                          </TableCell>
                                          <TableCell />
                                        </TableRow>
                                      </>
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {/* 🎯 PAYMENTS TOP BAR WITH YEAR DROPDOWN */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  background: "linear-gradient(to right, #ffffff, #f8fafc)",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalanceWalletOutlinedIcon sx={{ color: "#16a34a", fontSize: 24 }} />
                    <Typography variant="h6" fontWeight={700} color="#0f172a">
                      Payments Summary & Details
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedPaymentYear === "all"
                      ? "Aggregated summary for all academic years"
                      : `Selected Year: ${
                          years.find((b) => String(b.year?.id) === String(selectedPaymentYear))?.year?.name ||
                          selectedPaymentYear
                        }`}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 220, bgcolor: "#fff", borderRadius: 2 }}>
                    <InputLabel id="payment-year-select-label">Academic Year</InputLabel>
                    <Select
                      labelId="payment-year-select-label"
                      value={selectedPaymentYear || "all"}
                      label="Academic Year"
                      onChange={(e) => setSelectedPaymentYear(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">
                        <em>All Academic Years</em>
                      </MenuItem>
                      {years.map((b) => (
                        <MenuItem key={b.year?.id} value={String(b.year?.id)}>
                          {b.year?.name} {b.year?.code ? `(${b.year?.code})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => {
                      const targetYear =
                        selectedPaymentYear && selectedPaymentYear !== "all"
                          ? selectedPaymentYear
                          : years[0]?.year?.id || "";
                      setCreateDialogYear(targetYear);
                      openCreateDialog("payment");
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                    }}
                  >
                    Add Payment
                  </Button>
                </Box>
              </Paper>

              {/* 📊 PAYMENTS SUMMARY METRICS CARDS */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Total Collected"
                    value={`₹ ${fmtRs(paymentsSummary.paymentsTotal)}`}
                    subtitle={`${paymentsSummary.paymentsCount} recorded transaction${
                      paymentsSummary.paymentsCount === 1 ? "" : "s"
                    }`}
                    icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Total Orders"
                    value={`₹ ${fmtRs(paymentsSummary.ordersTotal)}`}
                    subtitle={`${paymentsSummary.ordersCount} service order${
                      paymentsSummary.ordersCount === 1 ? "" : "s"
                    } placed`}
                    icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Outstanding Balance"
                    value={`₹ ${fmtRs(Math.abs(paymentsSummary.balance))}`}
                    subtitle={
                      paymentsSummary.balance > 0
                        ? "Pending collection"
                        : paymentsSummary.balance < 0
                        ? "Advance received"
                        : "Fully settled"
                    }
                    icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                    background={
                      paymentsSummary.balance > 0
                        ? "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)"
                        : "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <OrderKpiCard
                    title="Payment Modes"
                    value={`${paymentsSummary.methodsList.length} ${
                      paymentsSummary.methodsList.length === 1 ? "Method" : "Methods"
                    }`}
                    subtitle={
                      paymentsSummary.methodsList.length > 0
                        ? paymentsSummary.methodsList.join(", ")
                        : "No transactions yet"
                    }
                    icon={<PaymentOutlinedIcon sx={{ fontSize: 20 }} />}
                    background="linear-gradient(135deg, #0d9488 0%, #0f766e 100%)"
                  />
                </Grid>
              </Grid>

              {/* 📋 PAYMENT DETAILS ACCORDING TO SELECTED YEAR */}
              {selectedPaymentYear !== "all" ? (
                // Single Selected Year View
                (() => {
                  const block = activePaymentBlocks[0];
                  if (!block) {
                    return (
                      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px dashed #cbd5e1" }}>
                        <Typography color="text.secondary">
                          No payment data available for the selected academic year.
                        </Typography>
                      </Paper>
                    );
                  }

                  const payments = block.payments || [];

                  return (
                    <Paper
                      elevation={1}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                          borderBottom: "1px solid #e2e8f0",
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Typography variant="h6" fontWeight={800} color="#0f172a">
                            {block.year?.name}
                          </Typography>
                          <Chip
                            label={block.has_setup ? "Setup Active" : "No Billing Setup"}
                            size="small"
                            color={block.has_setup ? "success" : "default"}
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            label={`Total Payments: ₹ ${fmtRs(block.payments_total)} · ${payments.length} record${
                              payments.length === 1 ? "" : "s"
                            }`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: "#f0fdf4",
                              color: "#16a34a",
                              border: "1px solid #bbf7d0",
                            }}
                          />
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<AddCircleOutlineOutlinedIcon />}
                          onClick={() => {
                            setCreateDialogYear(String(block.year?.id));
                            openCreateDialog("payment");
                          }}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                        >
                          Add Payment
                        </Button>
                      </Box>

                      <Box sx={{ p: 3 }}>
                        {!block.has_setup ? (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No billing setup available for {block.year?.name}.
                          </Alert>
                        ) : (
                          <>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                              <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                                Payment Details
                              </Typography>
                              <Chip
                                label={`${payments.length} record${payments.length === 1 ? "" : "s"}`}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>

                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {payments.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                        No payments recorded for {block.year?.name}
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    <>
                                      {payments.map((p, i) => (
                                        <TableRow
                                          key={p.id}
                                          hover
                                          sx={{
                                            cursor: "pointer",
                                            background: i % 2 === 0 ? "#ffffff" : "#fbfcfd",
                                          }}
                                          onClick={() => {
                                            if (block.year?.id && p.id) {
                                              navigate(
                                                `/billing/payment?year=${block.year.id}&company=${companyId}&payment_id=${p.id}`
                                              );
                                            }
                                          }}
                                        >
                                          <TableCell color="text.secondary">
                                            {p.payment_date ? String(p.payment_date).slice(0, 10) : "—"}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={p.method || "Payment"}
                                              size="small"
                                              variant="outlined"
                                              color="success"
                                              sx={{ fontSize: "0.75rem", height: 22 }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={p.status || "Completed"}
                                              sx={{ fontWeight: 600, fontSize: "0.75rem", height: 22 }}
                                            />
                                          </TableCell>
                                          <TableCell color="text.secondary">{p.reference || "—"}</TableCell>
                                          <TableCell color="text.secondary">{p.notes || "—"}</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700, color: "#16a34a" }}>
                                            ₹ {fmtRs(p.amount)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ background: "#f8fafc" }}>
                                        <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                                          Total Payments Received:
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: "#15803d", fontSize: "0.95rem" }}>
                                          ₹ {fmtRs(block.payments_total)}
                                        </TableCell>
                                      </TableRow>
                                    </>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </Box>
                    </Paper>
                  );
                })()
              ) : (
                // All Academic Years Accordion View
                years.map((block) => {
                  const payments = block.payments || [];
                  return (
                    <Accordion
                      key={`pay-${block.year?.id}`}
                      defaultExpanded={block === years[0]}
                      sx={{
                        mb: 2.5,
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography fontWeight={800} color="#0f172a">
                              {block.year?.name}
                            </Typography>
                            <Chip
                              label={block.has_setup ? "Setup Active" : "No Setup"}
                              size="small"
                              color={block.has_setup ? "success" : "default"}
                              sx={{ fontWeight: 600, fontSize: "0.7rem", height: 20 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Payments: ₹ {fmtRs(block.payments_total)} · {payments.length} record{payments.length === 1 ? "" : "s"}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ background: "#fff", p: 3 }}>
                        {!block.has_setup ? (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No billing setup available for {block.year?.name}
                          </Alert>
                        ) : (
                          <>
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                              <Grid item xs={12} md={6}>
                                <SummaryCard
                                  title="Total Payments"
                                  value={block.payments_total}
                                  color="linear-gradient(135deg,#22c55e,#16a34a)"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Card
                                  sx={{
                                    borderRadius: 3,
                                    color: "#fff",
                                    background: "linear-gradient(135deg,#0ea5e9,#0284c7)",
                                    boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <CardContent>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                      Payment records
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                      {payments.length}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Typography fontWeight={700} sx={{ mb: 1, color: "#1e293b" }}>
                              Payment details
                            </Typography>
                            <TableContainer sx={{ border: "1px solid #eef2f6", borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f8fafc" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {payments.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={6} align="center" sx={{ py: 2, color: "text.secondary" }}>
                                        No payments found
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    payments.map((p, i) => (
                                      <TableRow
                                        key={p.id}
                                        hover
                                        sx={{
                                          cursor: "pointer",
                                          background: i % 2 === 0 ? "#ffffff" : "#f9fafb",
                                        }}
                                        onClick={() => {
                                          if (block.year?.id && p.id) {
                                            navigate(
                                              `/billing/payment?year=${block.year.id}&company=${companyId}&payment_id=${p.id}`
                                            );
                                          }
                                        }}
                                      >
                                        <TableCell>
                                          {p.payment_date ? String(p.payment_date).slice(0, 10) : "—"}
                                        </TableCell>
                                        <TableCell>{p.method || "—"}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={p.status || "—"}
                                            sx={{ fontWeight: 600 }}
                                          />
                                        </TableCell>
                                        <TableCell>{p.reference || "—"}</TableCell>
                                        <TableCell>{p.notes || "—"}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                          ₹ {fmtRs(p.amount)}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Box>
          )}

          {activeTab === 3 && (
            <CompanyReportDetailsTab
              company={company}
              reports={reports}
              reportColumns={reportColumns}
              reportTabs={reportTabs}
              companyId={companyId}
            />
          )}

          {activeTab === 4 && (
            <Box>
              {teamReports.length === 0 ? (
                <Typography color="text.secondary">No team reports found.</Typography>
              ) : (
                teamReports.map((report) => (
                  <Card key={report.id} sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Submitted by: {report.created_by_name || "Unknown"} on {new Date(report.created).toLocaleString()}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Parameter</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.param_updates?.map((paramUpdate) => (
                            <TableRow key={paramUpdate.id}>
                              <TableCell>{paramUpdate.param_name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={paramUpdate.is_using ? "Using" : "Not Using"}
                                  size="small"
                                  color={paramUpdate.is_using ? "success" : "error"}
                                />
                              </TableCell>
                              <TableCell>{paramUpdate.reason || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
          {/* ─── VISIT SUMMARY TAB ─── */}
          {activeTab === 6 && (
            <VisitSummaryTab companyId={companyId} />
          )}
          {activeTab === 5 && (
            <Box>
              <CompanyChecklistTab company_id={companyId} company={company} />
            </Box>
          )}

          {activeTab === 7 && (
            <Box sx={{ p: 2}}>
              <Card variant="outlined" sx={{ mb: 3}}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3}}>
                    Last Synced Report Data
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#f8fafc" }} elevation={0}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                          {reports.length > 0 ? (reports[0]?.smsTotal || 0) : 0}
                          {/* {companyResources ? (companyResources[0]?.rows?.find(r => r.name === 'sms')?.max_limit || 0) : 0} */}
                        </Typography>
                        {/* <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all'}}>
                          {JSON.stringify(reports[0])}
                        </Typography> */}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8fafc' }} elevation={0}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Balance
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ mt: 1, fontWeight: 'bold'}}>
                          {reports.length > 0 ? (reports[0]?.sms_left || 0) : 0}
                          {/* {companyResources ? (companyResources[0]?.rows?.find(r => r.name === 'sms')?.available || 0) : 0} */}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper sx={{ p:3, textAlign: 'center', bgcolor: '#f8fafc'}} elevation={0}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Pending
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ mt: 1, fontWeight: 'bold'}}>
                          {reports.length > 0 ? (reports[0]?.sms_used || 0) : 0}
                          {/* {companyResources ? (companyResources[0]?.rows?.find(r => r.name === 'sms')?.usage || 0) : 0} */}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          <Dialog
            open={createDialogOpen}
            onClose={() => !createDialogLoading && setCreateDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Company: <strong>{company?.name}</strong>
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={createDialogYear}
                  label="Academic Year"
                  onChange={(e) => {
                    setCreateDialogYear(e.target.value);
                    resetSetupFields();
                  }}
                >
                  {years.map((block) => (
                    <MenuItem key={block.year?.id} value={block.year?.id}>
                      {block.year?.name}
                      {block.has_setup ? "" : " (no setup)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {needsBillingSetup && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    No billing setup exists for this year. Enter details below to create
                    one and continue to order creation.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student Strength"
                    type="number"
                    required
                    value={setupStudentStrength}
                    onChange={(e) => {
                      setSetupStudentStrength(e.target.value);
                      setSetupStudentStrengthError("");
                    }}
                    inputProps={{ min: 1, max: 9999, step: 1 }}
                    error={!!setupStudentStrengthError}
                    helperText={setupStudentStrengthError}
                    sx={{ mb: 2 }}
                  />
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    error={!!setupCoordinatorError}
                  >
                    <InputLabel>Coordinator</InputLabel>
                    <Select
                      value={setupCoordinator}
                      label="Coordinator"
                      onChange={(e) => {
                        setSetupCoordinator(e.target.value);
                        setSetupCoordinatorError("");
                      }}
                    >
                      {coordinators.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.username || u.email || u.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {setupCoordinatorError && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      {setupCoordinatorError}
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCreateDialogOpen(false)}
                disabled={createDialogLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateDialogContinue}
                disabled={createDialogLoading || !createDialogYear}
              >
                {dialogContinueLabel}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={downloadDialogOpen}
            onClose={handleDownloadDialogClose}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600 }}>Print invoice PDF</DialogTitle>
            <DialogContent>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Show GST</InputLabel>
                <Select
                  value={downloadShowGst ? "yes" : "no"}
                  label="Show GST"
                  onChange={(e) => setDownloadShowGst(e.target.value === "yes")}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Show Discount</InputLabel>
                <Select
                  value={downloadShowDiscount ? "yes" : "no"}
                  label="Show Discount"
                  onChange={(e) => setDownloadShowDiscount(e.target.value === "yes")}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Add Billing Summary</InputLabel>
                <Select
                  value={downloadShowBillingSummary ? "yes" : "no"}
                  label="Add Billing Summary"
                  onChange={(e) =>
                    setDownloadShowBillingSummary(e.target.value === "yes")
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              {downloadShowBillingSummary ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  sx={{ mt: 2 }}
                  label="Billing Summary"
                  value={downloadBillingSummary}
                  onChange={(e) => setDownloadBillingSummary(e.target.value)}
                  placeholder="Type billing summary text to include in PDF..."
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDownloadDialogClose} disabled={downloadLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadInvoiceSubmit}
                disabled={downloadLoading}
              >
                {downloadLoading ? "Preparing…" : "Print / Download"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}